// Stability AI Service

// Simple type for style presets
export type StabilityAIStylePreset =
  | 'photographic'
  | 'digital-art'
  | 'cinematic'
  | 'comic-book'
  | 'analog-film'
  | 'anime'
  | 'enhance'
  | 'fantasy-art'
  | 'isometric'
  | 'line-art'
  | 'low-poly'
  | 'modeling-compound'
  | 'neon-punk'
  | 'origami'
  | 'pixel-art'
  | '3d-model'
  | 'tile-texture';

// Simple parameters interface
export interface StabilityAIParams {
  prompt: string;
  negativePrompt?: string;
  stylePreset?: StabilityAIStylePreset;
}

// Simple response interface
export interface StabilityAIResponse {
  images: {
    base64: string;
    seed: number;
  }[];
  error?: string;
}

// Generate image function
export const generateImage = async (
   params: StabilityAIParams,
   abortSignal?: AbortSignal
): Promise<StabilityAIResponse> => {
   try {
      // Get API key
      const apiKey = process.env.REACT_APP_STABILITY_AI_API_KEY;

      if (!apiKey) {
         throw new Error('API key missing');
      }

      // API call configuration
      const response = await fetch(
         'https://api.stability.ai/v1/generation/stable-diffusion-xl-1024-v1-0/text-to-image',
         {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               Accept: 'application/json',
               Authorization: `Bearer ${apiKey}`,
            },
            body: JSON.stringify({
               text_prompts: [
                  { text: params.prompt, weight: 1 },
                  { text: params.negativePrompt || '', weight: -1 },
               ],
               cfg_scale: 7,
               height: 1024,
               width: 1024,
               samples: 1,
               steps: 30,
               style_preset: params.stylePreset || 'photographic',
            }),
            signal: abortSignal,
         }
      );

      // Handle errors
      if (!response.ok) {
         const errorData = await response.json();
         throw new Error(errorData.message || 'API error');
      }

      // Process response
      const data = await response.json();
      return {
         images: data.artifacts.map((item: any) => ({
            base64: item.base64,
            seed: item.seed,
         })),
      };
   } catch (error: any) {
      // If this is an AbortError, rethrow it to be handled by the caller
      if (error.name === 'AbortError') {
         throw error;
      }

      console.error('Image generation error:', error);
      return {
         images: [],
         error: error.message,
      };
   }
};

// Create prompt function
export const createEventCoverPrompt = (
   title: string,
   description?: string,
   type?: string,
   additionalText?: string
): string => {
   let prompt = `Professional event cover photo for "${title}"`;

   if (type) {
      prompt += `, ${type} event`;
   }

   if (description) {
      const shortDesc = description
         .replace(/<[^>]*>/g, '')
         .slice(0, 100)
         .trim();

      if (shortDesc) {
         prompt += `, featuring ${shortDesc}`;
      }
   }

   if (additionalText) {
      prompt += `. ${additionalText}`;
   }

   prompt += `. High-quality, professional photography, vibrant colors`;

   return prompt;
};

// Negative prompt function
export const getEventImageNegativePrompt = (): string => {
   return 'blurry, distorted, low-quality, pixelated, ugly, text, watermark';
};

// Convert base64 to File
export const base64ToFile = (base64String: string, fileName: string): File => {
   try {
      // Convert base64 string to a Blob
      const byteString = atob(base64String);
      const mimeType = 'image/png'; // Assuming PNG format
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);

      for (let i = 0; i < byteString.length; i++) {
         ia[i] = byteString.charCodeAt(i);
      }

      const blob = new Blob([ab], { type: mimeType });

      // Create a File from the Blob
      const file = new File([blob], fileName, { type: mimeType });

      // Log success for debugging

      return file;
   } catch (error) {
      console.error('Error converting base64 to File:', error);
      // Fallback to creating a simple blob-based file
      try {
         // Try a simpler approach
         const byteCharacters = atob(base64String);
         const byteArrays = [];

         for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
               byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
         }

         const blob = new Blob(byteArrays, { type: 'image/png' });
         const fallbackFile = new File([blob], fileName, { type: 'image/png' });

         return fallbackFile;
      } catch (fallbackError) {
         console.error('Even fallback method failed:', fallbackError);
         throw new Error('Could not convert image data to file');
      }
   }
};

/**
 * Generates a social media post image based on a prompt
 */
export const generateSocialMediaImage = async (
   prompt: string,
   platform?: string
): Promise<string> => {
   try {
      // If we have the generateImage function, use it
      if (typeof generateImage === 'function') {
         const result = await generateImage({
            prompt,
            negativePrompt: 'blurry, distorted, ugly, bad quality',
            stylePreset: 'photographic',
         });

         // Return the first image URL
         if (result && result.images && result.images.length > 0) {
            return `data:image/png;base64,${result.images[0].base64}`;
         }
      }

      // Fallback to using our placeholder image function
      return getPlaceholderImage(platform);
   } catch (err) {
      console.error('Error generating social media image:', err);
      // Return a placeholder image
      return getPlaceholderImage(platform);
   }
};

/**
 * Generates social media posts for different platforms based on event details
 */
export const generateSocialMediaPosts = async ({
   platforms,
   eventDetails,
   postsPerPlatform = 1,
}: {
   platforms: string[];
   eventDetails: {
      title: string;
      description: string;
      type: string;
      date: Date;
      location: string;
   };
   postsPerPlatform: number;
}): Promise<
   Array<{
      platform: string;
      content: string;
      imagePrompt: string;
      imageUrl: string;
      hashtags: string[];
   }>
> => {
   try {
      // Generate posts for each platform
      const posts = [];

      for (const platform of platforms) {
         for (let i = 0; i < postsPerPlatform; i++) {
            const platformSpecificContent = generatePlatformContent(platform, eventDetails);

            // Generate image prompt based on event details and platform
            const imagePrompt = `Event promotional image for ${eventDetails.title} at ${eventDetails.location}, optimized for ${platform}`;

            // Generate image URL
            const imageUrl = await generateSocialMediaImage(imagePrompt, platform);

            posts.push({
               platform,
               content: platformSpecificContent.content,
               imagePrompt,
               imageUrl,
               hashtags: platformSpecificContent.hashtags,
            });
         }
      }

      return posts;
   } catch (error) {
      console.error('Error in generateSocialMediaPosts:', error);

      // Provide fallback content in case of error
      const fallbackPosts = platforms.map((platform) => {
         const baseContent = `Join us for ${eventDetails.title} on ${new Date(eventDetails.date).toLocaleDateString()}!`;
         return {
            platform,
            content: baseContent,
            imagePrompt: `Event for ${eventDetails.title}`,
            imageUrl: '/assets/images/placeholder-event.jpg',
            hashtags: ['event', eventDetails.type.toLowerCase()],
         };
      });

      return fallbackPosts;
   }
};

// Helper function to generate platform-specific content
function generatePlatformContent(platform: string, eventDetails: any) {

  
  try {
    const { title, date, location, type } = eventDetails;
    const formattedDate = date instanceof Date ? 
      date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 
      new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    
    switch (platform) {
      case 'facebook':
        return {
          content: `Join us for ${title} on ${formattedDate} at ${location}! This ${type} event promises to be an unforgettable experience. Book your tickets now, limited spots available!`,
          hashtags: ['event', type.toLowerCase().replace(/\s+/g, ''), 'community', title.split(' ')[0].toLowerCase()]
        };
      
      case 'instagram':
        return {
          content: `✨ Mark your calendars! ${title} is happening on ${formattedDate} at ${location}. Don't miss this amazing ${type} event! Tag a friend you'd want to bring along. 📍`,
          hashtags: ['events', 'instaevents', type.toLowerCase().replace(/\s+/g, ''), 'experience', title.split(' ')[0].toLowerCase()]
        };
      
      case 'twitter':
        return {
          content: `Save the date! ${title} is coming to ${location} on ${formattedDate}. Join us for this incredible ${type} event. Limited tickets available!`,
          hashtags: ['event', type.toLowerCase().replace(/\s+/g, ''), 'savethedate']
        };
      
      case 'linkedin':
        return {
          content: `Announcing ${title}, a premier ${type} event happening on ${formattedDate} at ${location}. Network with industry professionals and gain valuable insights. Register now to secure your spot!`,
          hashtags: ['professional', 'networking', type.toLowerCase().replace(/\s+/g, ''), 'business']
        };
      
      default:
        return {
          content: `Join us for ${title} on ${formattedDate} at ${location}!`,
          hashtags: ['event', 'community']
        };
    }
  } catch (error) {
    console.error(`Error generating content for ${platform}:`, error);
    // Provide fallback content
    return {
      content: `Join us for an upcoming event!`,
      hashtags: ['event']
    };
  }
}

/**
 * Get placeholder image URL to use as fallback
 */
export const getPlaceholderImage = (platform?: string): string => {
  // Platform-specific placeholder images if available
  const platformImages: Record<string, string> = {
    facebook: '/assets/images/placeholders/facebook-post.jpg',
    instagram: '/assets/images/placeholders/instagram-post.jpg',
    twitter: '/assets/images/placeholders/twitter-post.jpg',
    linkedin: '/assets/images/placeholders/linkedin-post.jpg'
  };
  
  // Check if we have a platform-specific placeholder
  if (platform && platformImages[platform]) {
    return platformImages[platform];
  }
  
  // Generic event images to use as fallbacks
  const genericImages = [
    '/assets/images/placeholder-event.jpg',
    '/assets/images/event-placeholder.jpg',
    '/assets/images/event-cover.jpg'
  ];
  
  // Try each generic image in order
  for (const img of genericImages) {
    try {
      // Check if image exists by creating a new Image object
      const image = new Image();
      image.src = img;
      return img;
    } catch (err) {
      console.warn(`Placeholder image ${img} not available`);
      continue;
    }
  }
  
  // Final fallback - data URL for a simple colored square
  return 'data:image/png;base64,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';
};
